import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import RouteSeo from '../seo/RouteSeo';
import { useNavigate } from 'react-router-dom';
import {
  GraduationCap, Clock, ListChecks, Play, ArrowRight,
  PenLine, Globe2, BookOpen, Layers,
  FileUp, FileText, ChevronRight, ChevronDown, Sparkles,
} from 'lucide-react';
import { TOPICS } from '../data/helpTopics';
import { useArticleList } from '../hooks/useArticleList';
import { articlePath, topicPath } from '../seo/helpMeta';
import '../App.css';

const POPULAR = [
  { t: 'Set up the AI writing assistant', tag: 'Getting Started', icon: <Sparkles size={17} />, slug: 'assistant-setup' },
  { t: 'Import a Word document into Chaptery', tag: 'Getting Started', icon: <FileUp size={17} />, slug: 'import-word' },
  { t: 'How to build worlds', tag: 'World Building', icon: <Globe2 size={17} />, slug: 'build-worlds' },
  { t: 'Reuse a character across multiple books', tag: 'Series', icon: <Layers size={17} />, slug: 'reuse-entities' },
  { t: 'Add front & back matter to your book', tag: 'Book Design', icon: <BookOpen size={17} />, slug: 'front-back-matter' },
  { t: 'Export a print-ready PDF', tag: 'Export', icon: <FileText size={17} />, slug: 'export-pdf' },
  { t: 'Turn on distraction-free focus mode', tag: 'Editor', icon: <PenLine size={17} />, slug: 'focus-mode' },
];

interface Faq { q: string; a: React.ReactNode; }
const FAQS: Faq[] = [
  { q: 'Is Chaptery free?',
    a: <span>Chaptery has a free one-month trial just for signing up — no credit card required. Create an account and download the app for your operating system. After the trial, a subscription keeps you writing and syncing; either way, you'll always be able to open your local files and <strong>export your manuscripts</strong>.</span> },
  { q: 'Does Chaptery work offline?',
    a: <span>Chaptery is built to be used signed in, with your work backing up to your cloud <strong>Bookshelf</strong> and staying in sync across your computers. A dedicated offline mode isn't available today, so you'll want a connection while you write.</span> },
  { q: 'Can I import a manuscript I already started in Word?',
    a: <span>Absolutely. Choose <strong>Import Word Document</strong> from the welcome screen (or the Import menu once a book is open), pick your <code>.docx</code>, and Chaptery converts it to a Chaptery book — detecting your chapters automatically. It's one click, no wizard.</span> },
  { q: 'What can I export to?',
    a: <span>Chaptery exports a clean, typeset <strong>print-ready PDF</strong>, a reflowable <strong>EPUB</strong>, and <strong>Word</strong> — no separate publishing tool needed. For print, choose your trim size and settings when you export, then preview and go.</span> },
  { q: 'Can I use one character across several books?',
    a: <span>Yes — that's what world notes are for. Entities are shared across a <strong>series</strong>, so a character you define once can be referenced in every book they appear in, and filtered by book or series on the relationship map.</span> },
  { q: 'Where are my books stored, and are they backed up?',
    a: <span>Your books live on your machine and back up to your cloud <strong>Bookshelf</strong> on every save while you're signed in. You can reach your Bookshelf from any browser and pick up on any computer you sign in on.</span> },
];

const HelpCenterPage: React.FC = () => {
  const navigate = useNavigate();
  const { list } = useArticleList();
  const [openFaq, setOpenFaq] = useState<number>(0);

  return (
    <>
    <RouteSeo path="/help" />
    <Helmet>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": [
          { "@type": "Question", "name": "Is Chaptery free?", "acceptedAnswer": { "@type": "Answer", "text": "Chaptery has a free one-month trial just for signing up — no credit card required. After the trial, a monthly or annual subscription keeps you writing and syncing, and you can always open your local files and export your manuscripts." } },
          { "@type": "Question", "name": "Does Chaptery work offline?", "acceptedAnswer": { "@type": "Answer", "text": "Chaptery is built to be used signed in, with your work backing up to your cloud Bookshelf and staying in sync across your computers. A dedicated offline mode isn't available today, so you'll want a connection while you write." } },
          { "@type": "Question", "name": "Can I import a manuscript I already started in Word?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. Choose Import Word Document from the welcome screen or the Import menu, pick your .docx, and Chaptery converts it to a Chaptery book, detecting your chapters automatically. It is one click, no wizard." } },
          { "@type": "Question", "name": "What can I export to?", "acceptedAnswer": { "@type": "Answer", "text": "Chaptery exports a clean, typeset print-ready PDF, a reflowable EPUB, and Word — no separate publishing tool needed. For print, choose your trim size and settings when you export, then preview and go." } },
          { "@type": "Question", "name": "Can I use one character across several books?", "acceptedAnswer": { "@type": "Answer", "text": "Yes. The world builder shares characters, events, and locations across the books in a series, so your series stays consistent. Define a character once and reference it in every book it appears in, filtered by book or series on the relationship map." } },
          { "@type": "Question", "name": "Where are my books stored, and are they backed up?", "acceptedAnswer": { "@type": "Answer", "text": "Your books live on your machine and back up to your cloud Bookshelf on every save while you're signed in. You can reach your Bookshelf from any browser and pick up on any computer you sign in on, and you can also save locally as a .chaptery file or export to a supported format." } }
        ]
      })}</script>
      <script type="application/ld+json">{JSON.stringify({
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        "itemListElement": [
          { "@type": "ListItem", "position": 1, "name": "Chaptery", "item": "https://trychaptery.com" },
          { "@type": "ListItem", "position": 2, "name": "Help Center", "item": "https://trychaptery.com/help" }
        ]
      })}</script>
    </Helmet>
    <div className="help-page">

      <div className="help-crumbs">
        <a href="/">Chaptery</a><span className="sep">/</span><span>Help Center</span>
      </div>

      <section className="help-hero">
        <div className="help-hero-inner">
          <span className="eyebrow">Help Center</span>
          <h1>How can we help?</h1>
          <p>Guides, answers, and a full walkthrough for writing your book in Chaptery — from the first chapter to a print-ready PDF.</p>
        </div>
      </section>

      <div className="help-wrap">

        <section className="help-featured">
          <a className="help-featured-card" href="/help/tutorial" onClick={e => { e.preventDefault(); navigate('/help/tutorial'); }} style={{ textDecoration: 'none' }}>
            <div className="help-featured-text">
              <span className="help-featured-eyebrow"><GraduationCap size={14} /> Start here</span>
              <h2>The complete Chaptery walkthrough</h2>
              <p>Go from a blank page to a finished manuscript with a living world behind it — then export a print-ready PDF. Five stages, about twenty minutes.</p>
              <div className="help-featured-meta">
                <span><Clock size={13} /> ~20 min</span>
                <span><ListChecks size={13} /> 5 stages</span>
                <span><Play size={13} /> Video + steps</span>
              </div>
              <span className="help-featured-cta">Start the walkthrough <ArrowRight size={18} /></span>
            </div>
            <div className="help-featured-thumb">
              <div className="help-play" />
              <span className="help-featured-duration">13:53</span>
            </div>
          </a>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Browse by topic</h2></div>
          <div className="help-topics">
            {TOPICS.map(t => {
              const arts = list.filter(a => a.topic === t.title);
              return (
                <div className="help-topic-card" key={t.title}>
                  <span className={`help-topic-icon ${t.tone}`}>{t.icon}</span>
                  <h4>
                    <a
                      className="help-topic-title-btn"
                      href={topicPath(t.slug)}
                      onClick={e => { e.preventDefault(); navigate(topicPath(t.slug)); }}
                    >
                      {t.title}
                    </a>
                  </h4>
                  <p>{t.desc}</p>
                  <ul className="help-topic-articles">
                    {arts.slice(0, 4).map(a => (
                      <li key={a.slug}>
                        <a
                          href={articlePath(a.slug)}
                          onClick={e => { e.preventDefault(); navigate(articlePath(a.slug)); }}
                        >
                          <FileText size={13} /> {a.title}
                        </a>
                      </li>
                    ))}
                  </ul>
                  {arts.length > 0 && (
                    <a
                      className="help-topic-viewall"
                      href={topicPath(t.slug)}
                      onClick={e => { e.preventDefault(); navigate(topicPath(t.slug)); }}
                    >
                      View all {arts.length} article{arts.length === 1 ? '' : 's'} <ChevronRight size={14} />
                    </a>
                  )}
                </div>
              );
            })}
          </div>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Popular articles</h2></div>
          <ul className="help-popular-list">
            {POPULAR.map(p => (
              <li key={p.t}>
                <a href={articlePath(p.slug)} onClick={e => { e.preventDefault(); navigate(articlePath(p.slug)); }}>
                  <span className="pop-icon">{p.icon}</span>
                  {p.t}
                  <span className="pop-tag">{p.tag}</span>
                  <span className="pop-arrow"><ChevronRight size={16} /></span>
                </a>
              </li>
            ))}
          </ul>
        </section>

        <section className="help-section">
          <div className="help-section-head"><h2>Frequently asked</h2></div>
          <div className="help-faq-list">
            {FAQS.map((f, i) => (
              <div className={`help-faq-item ${openFaq === i ? 'open' : ''}`} key={f.q}>
                <button className="help-faq-q" onClick={() => setOpenFaq(openFaq === i ? -1 : i)}>
                  {f.q}
                  <span className="faq-chev"><ChevronDown size={20} /></span>
                </button>
                <div className="help-faq-a"><p>{f.a}</p></div>
              </div>
            ))}
          </div>
        </section>

        <section className="help-contact">
          <h3>Still need a hand?</h3>
          <p>Can't find what you're looking for? Our team is happy to help.</p>
          <div className="help-contact-actions">
            <a className="btn-primary" href="mailto:support@trychaptery.com" style={{ display: 'inline-block', padding: '10px 20px' }}>Email support</a>
            <a
              className="btn-secondary"
              href="/help/support"
              onClick={e => { e.preventDefault(); navigate('/help/support'); }}
              style={{ display: 'inline-block', padding: '10px 20px' }}
            >Help &amp; Refunds</a>
          </div>
        </section>

      </div>
    </div>
    </>
  );
};

export default HelpCenterPage;
